import { ApolloCache } from "@apollo/client"
import { stripTypename } from "@apollo/client/utilities"

import { MutationTypename } from "shared/api/types"
import { getObjectDifference } from "shared/lib/objectUtils"

import { didMove } from "entities/project-children/lib/didMove"
import { getChildPositionData } from "entities/project-children/lib/getChildMoveData"

import { getFloorComplete } from "../lib/getFloorComplete"
import { readFloorCompleteFragment } from "../lib/readFloorFragment"
import { FloorUpdate } from "../types/Floor"

export function prepareFloorUpdate({
  floor,
  displayPosition,
  cache,
  projectID,
  parentID,
  childType = "Floor"
}: {
  floor: FloorUpdate
  displayPosition?: number
  cache: ApolloCache<object>
  projectID: string
  parentID?: string | null
  childType?: "Floor"
}) {
  const cachedData = readFloorCompleteFragment(floor.id, cache)

  if (!cachedData) {
    throw new Error(`Unable to read cached data for Floor: ${floor.id}`)
  }

  const { children, ...floorWithoutChildren } = cachedData
  const { children: updatedChildren, ...floorUpdateWithoutChildren } = floor

  const changedValues = getObjectDifference(
    floorUpdateWithoutChildren,
    floorWithoutChildren || {}
  )

  const updatedFloor = {
    ...cachedData,
    ...changedValues
  }

  const moveData = getChildPositionData({
    projectID,
    parentID,
    childID: floor.id,
    childType,
    displayPosition,
    cache
  })

  const optimisticFloor = getFloorComplete({
    ...updatedFloor,
    position:
      moveData.nextPosition !== -1 ? moveData.nextPosition : cachedData.position
  })

  return {
    changedValues,
    input: stripTypename({
      id: floor.id,
      ...changedValues,
      position: didMove(moveData) ? moveData.nextPosition : undefined,
      parent:
        didMove(moveData) && moveData.prevParentID !== moveData.nextParentID
          ? {
              id: moveData.nextParentID,
              type: moveData.nextParentType
            }
          : undefined
    }),
    optimisticResponse: optimisticFloor
      ? { __typename: MutationTypename, floor: optimisticFloor }
      : undefined,
    children,
    moveData
  }
}
